// pages/configuration/ProductsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch,
  CircularProgress, Alert, Tooltip, alpha, Fade, Card, Stack, MenuItem,
  Select, FormControl, InputLabel, FormHelperText, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Inventory as ProductIcon, Close as CloseIcon,
  CheckCircle as ActiveIcon, Cancel as InactiveIcon, Category as CategoryIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { tradesService, Product, ProductPayload, ProductCategory } from '../../services/tradesService';

const UOM_OPTIONS = ['Pcs', 'Kg', 'Gram', 'Litre', 'ML', 'Meter', 'CM', 'Box', 'Dozen', 'Pair', 'Set', 'Pack'];
const LITRE_OPTIONS = [0.5, 1, 2, 4, 5, 8, 15, 16, 18, 20, 208];

// "2.000" -> "2", "0.500" -> "0.50"
const formatLitre = (v: string | number | null | undefined): string | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const usePermissions = () => {
  const { user } = useAuth();
  const perms = user?.role?.permissions?.[0];
  const isDataManager = user?.role?.role_name === 'DATA_MANAGER';
  return {
    canCreate: perms?.create_permission ?? false,
    canUpdate: perms?.update_permission ?? false,
    canDelete: (perms?.delete_permission ?? false) || isDataManager || (user?.is_superuser ?? false),
  };
};

// ── Form Dialog ───────────────────────────────────────────────────────────────
interface FormValues {
  product_name: string;
  category: number | '';
  unit_of_measure: string;
  litre_per_unit: number | '';  
  is_child_product: boolean;
  parent_product_id: number | '';
  parent_product_quantity: number | '';
  description: string;
  is_active: boolean;
}

const ProductFormDialog: React.FC<{
  open: boolean; editing: Product | null;
  categories: ProductCategory[]; allProducts: Product[];
  onClose: () => void; onSaved: () => void;
}> = ({ open, editing, categories, allProducts, onClose, onSaved }) => {
  const [saving, setSaving]     = useState(false);
  const [apiError, setApiError] = useState('');

  const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { product_name: '', category: '', unit_of_measure: 'Pcs', litre_per_unit: '', is_child_product: false,
      parent_product_id: '', parent_product_quantity: '', description: '', is_active: true },
  });

  const isChildProduct = watch('is_child_product');

  useEffect(() => {
    if (open) {
      setApiError('');
      if (editing) {
        reset({
          product_name: editing.product_name,
          category: editing.category ?? '',
          unit_of_measure: editing.unit_of_measure,
          litre_per_unit: editing.litre_per_unit != null ? Number(editing.litre_per_unit) : '',  
          is_child_product: editing.is_child_product,
          parent_product_id: editing.parent_product_id ?? '',
          parent_product_quantity: editing.parent_product_quantity ?? '',
          description: editing.description || '',
          is_active: editing.is_active,
        });
      } else {
        reset({ product_name: '', category: '', unit_of_measure: 'Pcs', litre_per_unit: '', is_child_product: false,
          parent_product_id: '', parent_product_quantity: '', description: '', is_active: true });
      }
    }
  }, [open, editing, reset]);

  // Clear parent_product_id when is_child_product toggled off
  useEffect(() => {
    if (!isChildProduct) setValue('parent_product_id', '');
    if (!isChildProduct) setValue('parent_product_quantity', '');
  }, [isChildProduct, setValue]);

  const onSubmit = async (data: FormValues) => {
    setSaving(true); setApiError('');
    try {
      const payload: ProductPayload = {
        product_name: data.product_name.trim(),
        category: data.category === '' ? null : Number(data.category),
        unit_of_measure: data.unit_of_measure,
        litre_per_unit: data.litre_per_unit === '' ? null : Number(data.litre_per_unit), 
        is_child_product: data.is_child_product,
        parent_product_id: data.is_child_product && data.parent_product_id !== ''
          ? Number(data.parent_product_id) : null,
        parent_product_quantity: data.is_child_product && data.parent_product_quantity !== ''
          ? Number(data.parent_product_quantity) : null,
        description: data.description.trim(),
        is_active: data.is_active,
      };
      if (editing) await tradesService.updateProduct(editing.product_id, payload);
      else await tradesService.createProduct(payload);
      onSaved(); onClose();
    } catch (e: any) {
      const errs = e?.data?.errors;
      setApiError(
        errs?.product_name?.[0] || errs?.parent_product_id?.[0] ||
        errs?.non_field_errors?.[0] || e?.data?.message || 'Something went wrong.'
      );
    } finally { setSaving(false); }
  };

  // Exclude current product from "reduced from" options
  const reducibleProducts = allProducts.filter(p =>
    !p.is_child_product && (!editing || p.product_id !== editing.product_id)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ProductIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            {editing ? 'Edit Product' : 'Add New Product'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}><CloseIcon /></IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {apiError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{apiError}</Alert>}
          <Stack spacing={2.5}>
            <Controller name="product_name" control={control}
              rules={{ required: 'Product name is required' }}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Product Name *" error={!!errors.product_name}
                  helperText={errors.product_name?.message}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller name="category" control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>Category</InputLabel>
                    <Select {...field} label="Category">
                      <MenuItem value=""><em>None</em></MenuItem>
                      {categories.filter(c => c.is_active).map(c => (
                        <MenuItem key={c.category_id} value={c.category_id}>{c.category_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />

              <Controller name="unit_of_measure" control={control}
                rules={{ required: 'UOM is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.unit_of_measure}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>Unit *</InputLabel>
                    <Select {...field} label="Unit *">
                      {UOM_OPTIONS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </Select>
                    {errors.unit_of_measure && <FormHelperText>{errors.unit_of_measure.message}</FormHelperText>}
                  </FormControl>
                )} />
              <Controller name="litre_per_unit" control={control}
                render={({ field }) => (
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    <InputLabel>Litre / Unit</InputLabel>
                    <Select {...field} label="Litre / Unit">
                      <MenuItem value=""><em>None</em></MenuItem>
                      {LITRE_OPTIONS.map(v => (
                        <MenuItem key={v} value={v}>{formatLitre(v)} L</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
            </Stack>

            <Divider sx={{ borderColor: alpha('#667eea', 0.1) }} />

            <Controller name="is_child_product" control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#667eea' } }} />}
                  label={<Typography variant="body2" fontWeight={600}>Is Child Product</Typography>}
                />
              )} />

              {isChildProduct && (
                <>
                  <Controller 
                    name="parent_product_id" 
                    control={control}
                    rules={{ required: isChildProduct ? 'Select a source product' : false }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.parent_product_id}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                        <InputLabel>Parent Product *</InputLabel>
                        <Select {...field} label="Parent Product *">
                          <MenuItem value=""><em>Select...</em></MenuItem>
                          {reducibleProducts.map(p => (
                            <MenuItem key={p.product_id} value={p.product_id}>{p.product_name}</MenuItem>
                          ))}
                        </Select>
                        {errors.parent_product_id && <FormHelperText>{errors.parent_product_id.message}</FormHelperText>}
                      </FormControl>
                    )} 
                  />             
                  <Controller 
                    name="parent_product_quantity" 
                    control={control}
                    rules={{ required: isChildProduct ? 'Please select a quantity' : false }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.parent_product_quantity}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                        <InputLabel>Parent Product Quantity (Litres)*</InputLabel>
                        <Select {...field} label="Parent Product Quantity *">
                          <MenuItem value="4">4 Litres</MenuItem>
                          <MenuItem value="5">5 Litres</MenuItem>
                        </Select>
                        {errors.parent_product_quantity && <FormHelperText>{errors.parent_product_quantity.message}</FormHelperText>}
                      </FormControl>
                    )} 
                  />
                </>
              )}

            <Controller name="description" control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Description" multiline rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />

            <Controller name="is_active" control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#667eea' } }} />}
                  label={<Typography variant="body2" fontWeight={600}>{field.value ? 'Active' : 'Inactive'}</Typography>}
                />
              )} />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} disabled={saving} variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha('#667eea', 0.4), color: '#667eea' }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ── Delete Dialog ─────────────────────────────────────────────────────────────
const DeleteDialog: React.FC<{
  open: boolean; item: Product | null; onClose: () => void; onDeleted: () => void;
}> = ({ open, item, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState('');

  const handleDelete = async () => {
    if (!item) return;
    setDeleting(true); setError('');
    try { await tradesService.deleteProduct(item.product_id); onDeleted(); onClose(); }
    catch (e: any) { setError(e?.data?.message || 'Failed to delete.'); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '50%', background: alpha('#f44336', 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <DeleteIcon sx={{ color: '#f44336', fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} mb={1}>Delete Product</Typography>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete <strong>"{item?.product_name}"</strong>?
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, textAlign: 'left' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={deleting} sx={{ borderRadius: 2, minWidth: 100 }}>Cancel</Button>
        <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: 2, minWidth: 100 }}>
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const ProductsPage: React.FC = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();

  const [products, setProducts]     = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterCat, setFilterCat]   = useState('');

  const [formOpen, setFormOpen]     = useState(false);
  const [editing, setEditing]       = useState<Product | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting]     = useState<Product | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (filterActive !== '') params.is_active = filterActive === 'true';
      if (filterCat) params.category_id = filterCat;
      const res = await tradesService.listProducts(params);
      setProducts(res.results);
    } catch { } finally { setLoading(false); }
  }, [search, filterActive, filterCat]);

  useEffect(() => {
    tradesService.listCategories({ is_active: true }).then(r => setCategories(r.results)).catch(() => {});
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  return (
    <Fade in timeout={400}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" fontWeight={800}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Products
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>Manage product catalogue</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchProducts} size="small"
                sx={{ border: `1px solid ${alpha('#667eea', 0.3)}`, color: '#667eea' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setFormOpen(true); }} size="small"
                sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102,126,234,0.3)' }}>
                Add Product
              </Button>
            )}
          </Stack>
        </Box>

        {/* Filters */}
        <Card sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${alpha('#667eea', 0.1)}` }} elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
            <TextField size="small" placeholder="Search products..." value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <FormControl size="small" sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel>Category</InputLabel>
              <Select value={filterCat} label="Category" onChange={e => setFilterCat(e.target.value as string)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {categories.map(c => <MenuItem key={c.category_id} value={c.category_id}>{c.category_name}</MenuItem>)}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1}>
              {[{ label: 'All', value: '' }, { label: 'Active', value: 'true' }, { label: 'Inactive', value: 'false' }].map(f => (
                <Chip key={f.value} label={f.label} size="small" clickable onClick={() => setFilterActive(f.value)}
                  sx={{ fontWeight: 600, borderRadius: 1.5,
                    background: filterActive === f.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : alpha('#667eea', 0.07),
                    color: filterActive === f.value ? 'white' : '#667eea' }} />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {products.length} result{products.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        </Card>

        {/* Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha('#667eea', 0.1)}`, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: alpha('#667eea', 0.04) }}>
                  {['#', 'Product Name', 'Category', 'UOM', 'Is Reduced', 'Status', 'Created By', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary',
                      py: 1.5, borderBottom: `2px solid ${alpha('#667eea', 0.12)}`, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={32} sx={{ color: '#667eea' }} />
                  </TableCell></TableRow>
                ) : products.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <ProductIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography variant="body2" color="text.secondary">No products found</Typography>
                  </TableCell></TableRow>
                ) : products.map((p, idx) => (
                  <TableRow key={p.product_id}
                    sx={{ '&:hover': { background: alpha('#667eea', 0.03) }, transition: 'background 0.2s' }}>
                    <TableCell sx={{ py: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 600, fontSize: '0.875rem', maxWidth: 180 }}>
                      <Typography noWrap title={p.product_name}>{p.product_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {p.category_detail ? (
                        <Chip size="small" icon={<CategoryIcon style={{ fontSize: 12 }} />}
                          label={p.category_detail.category_name}
                          sx={{ background: alpha('#9C27B0', 0.08), color: '#9C27B0', fontWeight: 600, height: 22, fontSize: '0.72rem' }} />
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip size="small"
                        label={
                          formatLitre(p.litre_per_unit)
                            ? `${p.unit_of_measure} / ${formatLitre(p.litre_per_unit)} L`
                            : p.unit_of_measure
                        }
                        sx={{ background: alpha('#2196F3', 0.08), color: '#2196F3', fontWeight: 600, height: 22, fontSize: '0.72rem' }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {p.is_child_product ? (
                        <Tooltip title={p.parent_product_id_name ? `From: ${p.parent_product_id_name}` : ''}>
                          <Chip size="small" label="Yes"
                            sx={{ background: alpha('#FF9800', 0.1), color: '#FF9800', fontWeight: 700, height: 22, fontSize: '0.72rem' }} />
                        </Tooltip>
                      ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip size="small"
                        icon={p.is_active ? <ActiveIcon style={{ fontSize: 13 }} /> : <InactiveIcon style={{ fontSize: 13 }} />}
                        label={p.is_active ? 'Active' : 'Inactive'}
                        sx={{ background: p.is_active ? alpha('#4CAF50', 0.1) : alpha('#f44336', 0.1),
                          color: p.is_active ? '#4CAF50' : '#f44336', fontWeight: 700, height: 22, fontSize: '0.75rem' }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: 'text.secondary', fontSize: '0.78rem' }}>{p.created_by}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={0.5}>
                        {canUpdate && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditing(p); setFormOpen(true); }}
                              sx={{ color: '#667eea', '&:hover': { background: alpha('#667eea', 0.1) } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => { setDeleting(p); setDeleteOpen(true); }}
                              sx={{ color: '#f44336', '&:hover': { background: alpha('#f44336', 0.1) } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <ProductFormDialog open={formOpen} editing={editing} categories={categories} allProducts={products}
          onClose={() => setFormOpen(false)} onSaved={fetchProducts} />
        <DeleteDialog open={deleteOpen} item={deleting}
          onClose={() => setDeleteOpen(false)} onDeleted={fetchProducts} />
      </Box>
    </Fade>
  );
};

export default ProductsPage;